import { addDays, differenceInCalendarDays, format, isAfter, isBefore, isValid, parseISO, startOfDay } from 'date-fns';

import { SERVER_DATE_FORMAT } from 'constants/logic';
import { adjustDateForTimezone, parseISOWithTimezone } from 'helpers/intl-fns';

export const CALENDAR_MAX_RANGE_DAYS = 42;

export const INDICATOR_WINDOW_DAYS = {
  waiting: 14,
  unsynced: 14,
  noClasses: CALENDAR_MAX_RANGE_DAYS,
};

export const DAY_INDICATOR_CLASS_NAMES = {
  noClasses: 'date-picker-day--no-classes',
  waiting: 'date-picker-day--waiting',
};

export function getTodayInVenueTimezone(timeZone, now = new Date()) {
  return startOfDay(timeZone ? adjustDateForTimezone(now, timeZone) : now);
}

// Redux holds the schedule start date as a Date on some routes and as an ISO string on others,
// which is why DateControls normalizes it too before rendering.
export function getSelectedDate({ startDate, timeZone, now = new Date() }) {
  const date = startDate instanceof Date ? startDate : parseISOWithTimezone(startDate, timeZone);

  return isValid(date) ? date : getTodayInVenueTimezone(timeZone, now);
}

export function getIndicatorFetchRange({ visibleStart, visibleEnd, timeZone, now = new Date() }) {
  const today = getTodayInVenueTimezone(timeZone, now);
  const start = isBefore(visibleStart, today) ? today : visibleStart;

  if (isAfter(start, visibleEnd)) {
    return null;
  }

  const cappedEnd = addDays(start, CALENDAR_MAX_RANGE_DAYS - 1);
  const end = isAfter(visibleEnd, cappedEnd) ? cappedEnd : visibleEnd;

  return {
    startDate: format(start, SERVER_DATE_FORMAT),
    endDate: format(end, SERVER_DATE_FORMAT),
  };
}

export function getDisabledAfterDate({ timeZone, isManualStudio, now = new Date() }) {
  if (isManualStudio) {
    return undefined;
  }
  return addDays(getTodayInVenueTimezone(timeZone, now), INDICATOR_WINDOW_DAYS.unsynced - 1);
}

export function buildDayModifiers({ dayIndicators = {}, timeZone, now = new Date() }) {
  const today = getTodayInVenueTimezone(timeZone, now);
  const noClasses = [];
  const waiting = [];

  Object.entries(dayIndicators).forEach(([dateString, indicator]) => {
    const date = parseISO(dateString);
    const offset = differenceInCalendarDays(date, today);
    if (offset < 0) {
      return;
    }
    if (offset <= INDICATOR_WINDOW_DAYS.noClasses - 1 && !indicator.hasClasses) {
      noClasses.push(date);
    }
    if (offset <= INDICATOR_WINDOW_DAYS.waiting - 1 && indicator.waitlistCount > 0) {
      waiting.push(date);
    }
  });

  return { noClasses, waiting };
}

export function buildIndicatorMap(data = []) {
  return data.reduce((map, entry) => {
    if (entry?.date) {
      map[entry.date] = {
        hasClasses: Boolean(entry.hasClasses),
        waitlistCount: entry.waitlistCount ?? null,
      };
    }
    return map;
  }, {});
}
